import { useEffect } from "react";
import { AlertCircle, Check } from "lucide-react";
import { create } from "zustand";
import { cn } from "@/lib/cn";

interface Toast {
  id: number;
  tone: "ok" | "crit";
  message: string;
}

interface ToastState {
  items: Toast[];
  push: (tone: Toast["tone"], message: string) => void;
  dismiss: (id: number) => void;
}

const useToasts = create<ToastState>((set) => ({
  items: [],
  push: (tone, message) =>
    set((s) => ({ items: [...s.items, { id: Date.now() + Math.random(), tone, message }] })),
  dismiss: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

/** Confirma algo que ya ocurrio. Nunca sustituye a un error de campo. */
export const toast = {
  ok: (message: string) => useToasts.getState().push("ok", message),
  error: (message: string) => useToasts.getState().push("crit", message),
};

export function Toaster() {
  const items = useToasts((s) => s.items);
  const dismiss = useToasts((s) => s.dismiss);

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
    >
      {items.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast: t, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const id = setTimeout(onDismiss, 4000);
    return () => clearTimeout(id);
  }, [onDismiss]);

  const Icon = t.tone === "ok" ? Check : AlertCircle;

  return (
    <div
      role="status"
      onClick={onDismiss}
      className={cn(
        "pointer-events-auto flex max-w-[340px] cursor-pointer items-center gap-2.5",
        "rounded-md border border-line bg-raised py-2.5 pl-3 pr-4 text-ui shadow-lg",
        "border-l-[3px]",
        t.tone === "ok" ? "border-l-ok" : "border-l-crit",
      )}
    >
      <Icon className={cn("size-4 shrink-0", t.tone === "ok" ? "text-ok" : "text-crit")} />
      {t.message}
    </div>
  );
}
